/**
 * Live-update coordinator voor de Capacitor-app (port van NovaDirect).
 *
 * Levenscyclus:
 *  1. Bij app-boot (initLiveUpdates): notifyAppReady() bevestigt dat de
 *     huidige bundle werkt, de crash-watcher gaat aan, en de uitkomst van een
 *     eventuele vorige update-poging wordt bepaald (success → toast, revert →
 *     telemetrie + evt. blacklist).
 *  2. Kort daarna (en bij elke app-resume): vraag novacheck.nl of er een
 *     nieuwere bundle is voor ons kanaal. Zo ja → download in achtergrond.
 *  3. LiveUpdatePrompt toont een banner. Gebruiker tikt "Later" of "Bijwerken".
 *  4. "Bijwerken" → activeer bundle; capgo herlaadt de WebView zelf.
 *  5. "Later" → escalation: re-prompt na 4u / 2u / 1u. Na 3 dismissals of 24u
 *     sinds eerste detectie wordt de prompt mandatory.
 *
 * Het kanaal ('staging' | 'main') wordt at build-time ingebakken via
 * VUE_APP_LIVE_UPDATE_CHANNEL: debug/staging-builds pollen het staging-kanaal,
 * release-builds het main-kanaal. De live-update bundles worden per branch
 * gebouwd (zie .github/workflows/live-update.yml), dus een bundle bevat altijd
 * het kanaal van z'n eigen branch.
 */

import { reactive, readonly } from "vue";
import toast from "./toast";
import { installLiveUpdateCrashWatcher } from "./liveUpdateCrashWatch";
import {
    blacklistBlocks,
    blacklistedSince,
    clearBlacklistEntry,
    clearRevertCount,
    clearPendingReports,
    consumeUpdateOutcome,
    enqueueFailureReport,
    markBundleActivated,
    markJustUpdated,
    readDismiss,
    readPendingReports,
    writeDismiss,
} from "./liveUpdateState";

// Altijd de centrale site — releases zijn globaal, niet tenant-specifiek
// (zelfde host als updateService.js voor de store-versiecheck). Overridebaar
// voor lokaal testen (bv. http://10.0.2.2:3000 vanuit de Android-emulator).
const LIVE_UPDATE_BASE = process.env.VUE_APP_LIVE_UPDATE_BASE || "https://novacheck.nl";
const CHANNEL = process.env.VUE_APP_LIVE_UPDATE_CHANNEL === "staging" ? "staging" : "main";

const HARD_DEADLINE_MS = 24 * 60 * 60 * 1000; // 24u na firstSeenAt → mandatory
const MAX_SOFT_DISMISSES = 3; // na 3 dismisses → mandatory

const state = reactive({
    available: null, // LiveUpdateInfo | null
    ready: false,
    downloading: false,
    downloadProgress: -1, // 0–100, -1 = onbekend
    dismissedUntil: 0,
    mandatory: false,
    bundleId: null, // ID van de gedownloade bundle bij de plugin
    error: null, // download-fout (toont retry-knop)
    applying: false,
});

let progressListenerAttached = false;
let initialized = false;
let cachedDeviceId = null;

// Hoe lang we wachten voor we opnieuw mogen prompten na een dismiss.
function cooldownMs(count) {
    if (count <= 1) return 4 * 60 * 60 * 1000; // 4h
    if (count === 2) return 2 * 60 * 60 * 1000; // 2h
    return 1 * 60 * 60 * 1000; // 1h
}

function evaluateMandatory(version) {
    const d = readDismiss(version);
    const sinceFirst = Date.now() - (d.firstSeenAt || Date.now());
    state.mandatory = d.dismissCount >= MAX_SOFT_DISMISSES || sinceFirst >= HARD_DEADLINE_MS;
}

async function getPlatform() {
    try {
        const { Capacitor } = await import("@capacitor/core");
        const p = Capacitor.getPlatform();
        if (p === "android" || p === "ios") return p;
    } catch {
        /* web */
    }
    return "web";
}

/**
 * Stabiel toestel-ID voor install-/rollout-telemetrie op de server. Geen
 * persoonsgegevens; Capacitor Device.getId() geeft per app-installatie een
 * opaak identifier.
 */
async function getDeviceId() {
    if (cachedDeviceId) return cachedDeviceId;
    try {
        const { Device } = await import("@capacitor/device");
        const { identifier } = await Device.getId();
        cachedDeviceId = identifier || null;
    } catch {
        cachedDeviceId = null;
    }
    return cachedDeviceId;
}

/**
 * Flush de lokale wachtrij met mislukking-meldingen naar de server. Draait
 * vooraan in check(). Faalt stil: bij offline blijft de wachtrij staan voor
 * de volgende keer. Bewust fetch i.p.v. axios: de globale axios-interceptors
 * plakken de tenant-bearer en vnd.api+json-headers op elke request en die
 * horen niet thuis op novacheck.nl.
 */
async function flushFailureReports() {
    const pending = readPendingReports();
    if (!pending.length) return;
    try {
        const platform = await getPlatform();
        const deviceId = await getDeviceId();
        const events = pending.map((r) => ({ ...r, platform: r.platform || platform }));
        const res = await fetch(`${LIVE_UPDATE_BASE}/api/public/live-update/report`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ events, deviceId }),
        });
        if (!res.ok) throw new Error(`report failed (${res.status})`);
        clearPendingReports();
    } catch (e) {
        console.warn("[liveUpdate] flush failure-reports failed:", e);
    }
}

async function getCurrentBundleVersion() {
    try {
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
        const cur = await CapacitorUpdater.current();
        return (cur && cur.bundle && cur.bundle.version) || "builtin";
    } catch {
        return "builtin";
    }
}

/** Worker: vraag het endpoint, en download als er iets nieuws is. */
export async function check() {
    state.error = null;
    // Eerst openstaande mislukking-meldingen wegschrijven (best-effort).
    await flushFailureReports();

    let info = null;
    try {
        const platform = await getPlatform();
        const currentVersion = await getCurrentBundleVersion();
        const deviceId = await getDeviceId();
        // Harde timeout: zonder dit kan een hangende request de check eindeloos
        // laten staan. AbortController i.p.v. AbortSignal.timeout voor bredere
        // WKWebView-compatibiliteit.
        const ac = new AbortController();
        const to = setTimeout(() => ac.abort(), 15000);
        try {
            const params = new URLSearchParams({ cv: currentVersion, ch: CHANNEL, p: platform });
            if (deviceId) params.set("d", deviceId);
            const res = await fetch(`${LIVE_UPDATE_BASE}/api/public/live-update?${params}`, { signal: ac.signal });
            if (!res.ok) throw new Error(`check failed (${res.status})`);
            info = (await res.json()).update;
        } finally {
            clearTimeout(to);
        }
    } catch (e) {
        // Offline / timeout / 5xx — stil laten; de volgende check pakt het op.
        console.warn("[liveUpdate] check failed:", e);
        return;
    }

    if (!info) {
        state.available = null;
        state.ready = false;
        state.bundleId = null;
        return;
    }

    // Skip versies die lokaal zijn geblacklist (na mislukte activaties of door
    // de crash-watcher). Uitzondering: heeft de admin de release ná het
    // blacklisten OPNIEUW gepromoot (promotedAt > blacklist-tijd)? Dan is dat
    // een expliciet "geverifieerd, probeer opnieuw"-signaal — we wissen de
    // lokale blacklist + revert-teller zodat de versie weer een kans krijgt.
    const wasBlacklistedSince = blacklistedSince(info.version);
    if (blacklistBlocks(info.version, info.promotedAt)) {
        console.warn("[liveUpdate] skip blacklisted version", info.version);
        state.available = null;
        state.ready = false;
        state.bundleId = null;
        return;
    }
    if (wasBlacklistedSince !== null) {
        clearBlacklistEntry(info.version);
        clearRevertCount(info.version);
    }

    // Nieuwe versie binnen → reset dismiss-counter als het echt een andere is.
    const prev = readDismiss(info.version);
    if (prev.version !== info.version || prev.firstSeenAt === 0) {
        writeDismiss({ version: info.version, firstSeenAt: Date.now(), lastDismissedAt: null, dismissCount: 0 });
    }
    state.available = info;
    evaluateMandatory(info.version);

    await downloadBundle(info);
}

/**
 * Download de bundle bij de plugin en houd downloadProgress bij via het
 * 'download'-event.
 */
async function downloadBundle(info) {
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");

        // Hergebruik een al gedownloade kopie van deze versie (van een eerdere
        // check deze sessie of een vorige boot). check() draait bij elke
        // app-resume; zonder deze guard wordt dezelfde zip telkens opnieuw
        // gedownload en stapelen bundle-kopieën zich op het toestel op.
        // Capgo registreert een bundle pas ná volledige download + geslaagde
        // checksum-verificatie, dus een niet-error kopie is compleet en veilig
        // om te activeren.
        try {
            const list = await CapacitorUpdater.list();
            const existing = (list.bundles || []).find((b) => b.version === info.version && b.status !== "error");
            if (existing) {
                state.error = null;
                state.bundleId = existing.id;
                state.downloadProgress = 100;
                state.ready = true;
                return;
            }
        } catch {
            /* lijst niet beschikbaar — dan gewoon downloaden */
        }

        // Progress-event één keer koppelen; blijft de hele app-lifecycle staan.
        if (!progressListenerAttached) {
            progressListenerAttached = true;
            await CapacitorUpdater.addListener("download", (s) => {
                if (typeof s.percent === "number") {
                    state.downloadProgress = Math.max(0, Math.min(100, Math.round(s.percent)));
                }
            });
        }

        state.error = null;
        state.ready = false;
        state.downloadProgress = 0;
        state.downloading = true;
        // checksum = sha256(zip), hex — exact wat publish-live-update.mjs opslaat
        // en wat capgo native over de gedownloade zip berekent. Zonder publicKey
        // wordt 'ie 1-op-1 vergeleken, dus een corrupte/afgekapte download wordt
        // geweigerd vóór activatie — geen kapotte boot + revert.
        const bundle = await CapacitorUpdater.download({
            url: info.url,
            version: info.version,
            checksum: info.sha256 ? info.sha256.toLowerCase() : undefined,
        });
        state.bundleId = bundle.id;
        state.downloadProgress = 100;
        state.ready = true;
    } catch (e) {
        console.error("[liveUpdate] download failed:", e);
        state.error = "Update kon niet worden gedownload.";
        state.ready = false;
        // Telemetrie: download/verify mislukte — queue een melding zodat admin
        // ziet dat deze release niet eens te downloaden was.
        try {
            const fromVersion = await getCurrentBundleVersion();
            enqueueFailureReport({
                failedVersion: info.version,
                fromVersion,
                reason: "download_failed",
                detail: (e instanceof Error ? e.message : String(e)).slice(0, 200),
            });
        } catch {
            /* best-effort */
        }
    } finally {
        state.downloading = false;
    }
}

/** Gebruiker tikt "Opnieuw proberen" na een mislukte download. */
export async function retryDownload() {
    if (!state.available || state.downloading) return;
    await downloadBundle(state.available);
}

/** Gebruiker tikt "Later". */
export function dismiss() {
    if (!state.available || state.mandatory) return;
    const d = readDismiss(state.available.version);
    d.dismissCount += 1;
    d.lastDismissedAt = Date.now();
    writeDismiss(d);
    state.dismissedUntil = Date.now() + cooldownMs(d.dismissCount);
    evaluateMandatory(state.available.version);
}

/** Gebruiker tikt "Bijwerken". Activeer bundle; capgo herlaadt de WebView zelf. */
export async function applyNow() {
    if (!state.bundleId || state.applying) return;
    state.applying = true;
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
        // Stempel "activated at" zodat de crash-watcher kort na de reload weet
        // welke versie net is geactiveerd; markJustUpdated zorgt dat we ná de
        // reload een bevestiging tonen — maar alléén als deze versie dan ook
        // echt actief blijkt (geen rollback).
        if (state.available) {
            markBundleActivated(state.available.version);
            markJustUpdated(state.available.version);
        }
        // Alléén set() — GEEN handmatige reload() erna. set() is capgo's
        // terminale "activeer-nu"-operatie: het markeert de bundle atomair als
        // current, herlaadt de WebView zélf naar de nieuwe bundle, en wacht
        // intern (via een semaphore) tot de nieuwe bundle notifyAppReady()
        // aanroept. Een handmatige reload() erachteraan racet met die semaphore
        // en laat capgo een geslaagde activatie ten onrechte terugrollen.
        //
        // Awaiten doen we niet: bij een geslaagde activatie destroyt set() deze
        // JS-context vóór de promise resolved. Een false-positive timeout-fout
        // vangen we stil op.
        CapacitorUpdater.set({ id: state.bundleId }).catch((e) => {
            console.warn("[liveUpdate] set() reported (mogelijk false-positive na succesvolle activatie):", e);
        });
        // Indien de reload stil faalt geven we de UI na een veiligheidsmarge vrij.
        setTimeout(() => {
            state.applying = false;
        }, 8000);
    } catch (e) {
        console.error("[liveUpdate] apply failed:", e);
        state.error = "Bijwerken is mislukt — probeer het opnieuw.";
        state.applying = false;
    }
}

/**
 * Verzamel capgo-diagnostiek over een teruggerolde bundle voor de
 * server-telemetrie (bundle-status + native-/plugin-versie). Best-effort.
 */
async function collectRevertDiagnostics(failedVersion) {
    try {
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
        const parts = [];
        try {
            const list = await CapacitorUpdater.list();
            const b = (list.bundles || []).find((x) => x.version === failedVersion);
            parts.push(b ? `bundle=${b.status || "?"}` : "bundle=afwezig");
        } catch {
            /* ignore */
        }
        try {
            const cur = await CapacitorUpdater.current();
            if (cur && cur.native) parts.push(`native=${cur.native}`);
        } catch {
            /* ignore */
        }
        try {
            const pv = await CapacitorUpdater.getPluginVersion();
            if (pv && pv.version) parts.push(`plugin=${pv.version}`);
        } catch {
            /* ignore */
        }
        return parts.join(" · ");
    } catch {
        return "";
    }
}

/**
 * Bootstrap — aanroepen vanaf App.vue mounted (alleen native). Bevestigt de
 * draaiende bundle bij de plugin, start de crash-watcher, verwerkt de uitkomst
 * van een eventuele vorige update-poging en plant de checks (boot + resume).
 */
export async function initLiveUpdates() {
    if (initialized) return;
    initialized = true;
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
        await CapacitorUpdater.notifyAppReady();
        // Crash-watcher: trace runtime-errors voor 60s na bundle-activate; bij
        // ≥3 errors rolt 'ie terug naar de native-shipped bundle.
        installLiveUpdateCrashWatcher();

        const currentVersion = await getCurrentBundleVersion();

        // Ruim duplicaat-kopieën van de draaiende versie op (restanten van
        // eerdere downloads vóór de dedupe-guard). Een klaarstaande nieuwere
        // versie blijft staan zodat downloadBundle 'm kan hergebruiken.
        try {
            const cur = await CapacitorUpdater.current();
            const list = await CapacitorUpdater.list();
            for (const b of list.bundles || []) {
                if (b.id !== cur.bundle.id && b.version === currentVersion) {
                    await CapacitorUpdater.delete({ id: b.id }).catch(() => {});
                }
            }
        } catch {
            /* opruimen is best-effort */
        }

        // Uitkomst van een eventuele vorige update-poging bepalen t.o.v. wat nu
        // draait. Bij success: bevestiging. Bij revert blacklisten we de versie
        // na een paar pogingen, zodat de prompt niet eindeloos terugkomt.
        const outcome = consumeUpdateOutcome(currentVersion);
        if (outcome.status === "success") {
            toast.success("NovaCheck is bijgewerkt — je gebruikt nu de nieuwste versie.", 6000);
        } else if (outcome.status === "reverted") {
            // Telemetrie: élke teruggerolde poging melden (ook vóór blacklist),
            // zodat admin ziet wélke release op welk platform stukloopt en
            // waarom. Queue lokaal; flush gebeurt in check().
            const platform = await getPlatform();
            const diag = await collectRevertDiagnostics(outcome.version);
            const detail = [outcome.detail, diag].filter(Boolean).join(" · ").slice(0, 900) || undefined;
            enqueueFailureReport({
                failedVersion: outcome.version,
                fromVersion: currentVersion,
                reason: outcome.reason,
                detail,
                blacklisted: outcome.blacklisted,
                platform,
            });
            if (outcome.blacklisted) {
                toast.warning("De update kon niet worden geïnstalleerd. Je blijft voorlopig op de huidige versie; we proberen het bij een volgende versie opnieuw.", 7000);
            }
        }

        // Eerste check kort na boot; daarna bij elke app-resume.
        setTimeout(() => {
            check();
        }, 3000);

        const { App } = await import("@capacitor/app");
        await App.addListener("appStateChange", ({ isActive }) => {
            if (!isActive) return;
            check();
        });
    } catch (e) {
        console.warn("[liveUpdate] plugin init failed:", e);
    }
}

/** Reactieve, read-only state voor de prompt-component. */
export const liveUpdateState = readonly(state);

export function isPromptVisible() {
    // Er loopt een bundle-proces (download bezig, klaar, of gefaald). Alleen
    // dán tonen we de prompt.
    const inProgress = state.downloading || state.ready || !!state.error;
    if (!state.available || !inProgress) return false;
    if (state.mandatory) return true;
    return Date.now() >= state.dismissedUntil;
}
